// PageDescription: 대면진료 병원 페이지
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import HospitalNoticePopup from '@/components/templates/f2f/common/hospital-notice-popup';
import HospitalsHomeTemplate from '@/components/templates/f2f/hospital/hospitals-home-template';
import useHospitalNoticePopupViewModel from '@/hooks/hospital/list/useHospitalNoticePopupViewModel';
import useFaceToFaceTreatmentKeywords from '@/hooks/hospital/main/useFaceToFaceTreatmentKeywords';
import useMedicalCategories from '@/hooks/hospital/main/useMedicalCategories';
import { CustomPageProps } from '@/types/app';
import { MedicalCategorySortType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';

const HospitalsHomePage = ({ client }: CustomPageProps) => {
  const viewModel = useHospitalNoticePopupViewModel();
  return (
    <>
      <NextSeo
        title='병원 찾기 - 닥터나우'
        description='내 근처 병원을 전국 76,614개 병원이 모인 닥터나우에서 찾아보세요! 위치부터 후기까지 확인 후, 예약할 수 있어요!'
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.HOME.path}`}
        openGraph={{
          title: '병원 찾기 - 닥터나우',
          description: '병원 위치부터 후기까지 확인 후, 예약해 보세요!',
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_HOSPITAL_SEARCH_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <HospitalsHomeTemplate client={client} />
      <HospitalNoticePopup enabled={client.isWebView} viewModel={viewModel} />
    </>
  );
};

export default HospitalsHomePage;

HospitalsHomePage.Layout = DefaultLayout;

export const getServerSideProps: GetServerSideProps = async () => {
  const queryClient = new QueryClient();

  await Promise.allSettled([
    useMedicalCategories.prefetchQuery(queryClient, MedicalCategorySortType.Recommendation),
    useFaceToFaceTreatmentKeywords.prefetchQuery(queryClient),
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};
